import { Button } from '@odin-labs/components';
import { PlusIcon } from 'components/icons';
import React from 'react';

export type LayoutSidebarProps = {
  readerStripText: string;
  /** Delivery of the effective layout to Avigilon, and Sync now. */
  avigilon: React.ReactNode;
  zones: {
    count: number;
    canAdd: boolean;
    onAdd: () => void;
    children: React.ReactNode;
  };
  entries: {
    count: number;
    canAdd: boolean;
    addTitle?: string;
    onAdd: () => void;
    children: React.ReactNode;
  };
  removedNames: string[];
};

const classes = {
  card: 'odin-flex odin-w-60 odin-flex-shrink-0 odin-flex-col odin-overflow-y-auto odin-rounded-lg odin-border odin-border-gray-200 odin-bg-white',
  strip:
    'odin-flex odin-flex-col odin-gap-1 odin-rounded-t-lg odin-border-b odin-border-gray-200 odin-bg-gray-50 odin-px-4 odin-py-3',
  stripText: 'odin-text-xs odin-text-gray-700',
  sectionHeader:
    'odin-flex odin-items-center odin-justify-between odin-px-4 odin-pb-1 odin-pt-4',
  sectionTitle:
    'odin-text-xs odin-font-semibold odin-uppercase odin-tracking-wide odin-text-gray-500',
  list: 'odin-flex odin-flex-col odin-gap-0.5 odin-px-2',
  removed:
    'odin-px-3 odin-py-2 odin-text-sm odin-text-gray-400 odin-line-through',
  spacer: 'odin-h-3',
};

/**
 * The layout's table of contents: the site's readers at a glance, its
 * delivery to Avigilon, then its zones and entries as selectable rows, then
 * anything the draft removed.
 */
export function LayoutSidebar({
  readerStripText,
  avigilon,
  zones,
  entries,
  removedNames,
}: LayoutSidebarProps): React.ReactElement {
  return (
    <aside className={classes.card} aria-label="Layout contents">
      <div className={classes.strip}>
        <span className={classes.stripText}>{readerStripText}</span>
      </div>

      <section aria-label="Avigilon">
        <header className={classes.sectionHeader}>
          <h2 className={classes.sectionTitle}>Avigilon</h2>
        </header>
        {avigilon}
      </section>

      <section aria-label="Zones">
        <header className={classes.sectionHeader}>
          <h2 className={classes.sectionTitle}>Zones · {zones.count}</h2>
          <Button
            icon={PlusIcon}
            text="Add zone"
            size="xs"
            theme="naked"
            disabled={!zones.canAdd}
            onClick={zones.onAdd}
          />
        </header>
        <div className={classes.list}>{zones.children}</div>
      </section>

      <section aria-label="Entries">
        <header className={classes.sectionHeader}>
          <h2 className={classes.sectionTitle}>Entries · {entries.count}</h2>
          <span title={entries.addTitle}>
            <Button
              icon={PlusIcon}
              text="Add entry"
              size="xs"
              theme="naked"
              disabled={!entries.canAdd}
              onClick={entries.onAdd}
            />
          </span>
        </header>
        <div className={classes.list}>{entries.children}</div>
      </section>

      {removedNames.length > 0 && (
        <section aria-label="Removed in draft">
          <header className={classes.sectionHeader}>
            <h2 className={classes.sectionTitle}>
              Removed in draft · {removedNames.length}
            </h2>
          </header>
          <div className={classes.list}>
            {removedNames.map((name) => (
              <span key={name} className={classes.removed}>
                {name}
              </span>
            ))}
          </div>
        </section>
      )}

      <div className={classes.spacer} />
    </aside>
  );
}
